import React, { FC, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./Copilot.scss";
import ApiContext from "../../Api/ApiContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEventSubscriber } from "../../Api/ApiEvents/ApiEventSubscriber";
import { ReactSVG } from "react-svg";
import sendIcon from "../../Assets/icons/send.svg";
import Button, { EButtonSize, EButtonType } from "../../Components/Buttons/Button";
import keepIcon from "../../Assets/icons/check.svg";
import redraftIcon from "../../Assets/icons/redraft.svg";
import { useDispatch, useSelector } from "react-redux";
import { draftingSlice, selectRedraftingClause } from "../../Store/draftingSlice";
import { AppDispatch } from "../../Store/store";
import RestrictReadOnly from "../../Helpers/RestrictReadOnly";
import DOMPurify from "dompurify";
import i18n from "../../Locale/i18n";
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';

interface ICopilotProps {
    sourceType?: string;
    sourceId?: number;
    updateContent: (content: any) => void;
    clauses: any[];
}

const Copilot: FC<ICopilotProps> = ({ sourceType, sourceId, updateContent, clauses }) => {

    const [ history, setHistory ] = useState([]);
    const historyRef = useRef<HTMLDivElement | null>(null);
    const textareaRef = useRef(null);
    const [ streaming, setStreaming ] = useState(false);
    const [ fetchingData, setFetchingData ] = useState(true);

    const [ conversationId, setConversationId ] = useState(null);

    const redraftingClause = useSelector(selectRedraftingClause);
    const dispatch = useDispatch<AppDispatch>();

    const api = useContext(ApiContext);

    const { t } = useTranslation();

    const scrollToBottom = useCallback(() => {
        if(historyRef.current) {
            historyRef.current.scrollTop = historyRef.current.scrollHeight;
        }
    }, [historyRef]);

    useEffect(() => {
        textareaRef?.current?.focus();
    }, [textareaRef]);

    useLayoutEffect(() => {
        scrollToBottom();
    });

    const navigate = useNavigate();

    const getHistory = useCallback(async () => {

        if(!sourceType || !sourceId) return [];

        try {
          const response = await api.post(
            '/copilot/messages',
            { sourceType, sourceId }
          );

          setConversationId(response.data.id);
      
          const historyData = response.data.messages;
          
          return historyData;

        } catch (error) {
            return []; 
        }
    }, [api, sourceType, sourceId]);

    const redraft = useCallback(async(question, attachedContent, currentConversation, redraftingClause) => {

        let mostRecentHistory = history ?? [];
        mostRecentHistory = [...mostRecentHistory, { 
            question: {
                prompt: question,
            },
            answer: {
                reply: "",
            },
            status: "progress"
         }];

        setHistory(mostRecentHistory);

        setStreaming(true);

        api.post(`/copilot/${currentConversation}/instructions`,
            { 
                language: i18n.language,
                prompt: question,
                clause: redraftingClause,
                sourceId: sourceId
            }).then(async(res) => {

                setHistory(() => {
                    const newHistory = [...mostRecentHistory];
                    newHistory[newHistory.length - 1].eventListenerId = res.data.eventPublisher;
                    return newHistory;
                });

            });

    }, [history, api, getHistory, navigate, conversationId]);

    useEffect(() => {
        getHistory().then((history) => {
            setHistory(history);
            setFetchingData(false);
        });
    }, [getHistory]);

    const InitialMessage = () => {

        return (
            <div className="fly-conversation">
                <div className="answer">
                    <div className="fly-questionBubble">
                        <div className="text">
                            <p>
                                {t("copilot.hey")} 👋<br/>
                                {t("copilot.intro_4")} 👔 💼
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const Answer = (props: any) => {

        const { element } = props;

        const [ localAnswer, setLocalAnswer ] = useState(element?.answer?.reply || "");

        useEffect(() => {
            setLocalAnswer(element?.answer?.reply || "");
        }, [element?.answer?.reply]);
    
        const unescapeHTML = (html) => {
            const txt = document.createElement('textarea');
            txt.innerHTML = html;
            return txt.value;
        }

        const [content, setContent] = useState(
            localAnswer === "" ? 
            t("fly.wait") : 
            unescapeHTML(localAnswer)
        );

        useEffect(() => {
            setContent(
                localAnswer === "" ? 
                `<div class="loader"></div>` : 
                unescapeHTML(localAnswer)
            );
        }, [localAnswer]);

        useEventSubscriber("stream", element?.eventListenerId, (data: any) => {

            if(data?.status === "done") {
                setStreaming(false);
                const newHistory = [...history];
                newHistory[newHistory.length - 1] = {
                    ...newHistory[newHistory.length - 1],
                    answer: {
                        ...newHistory[newHistory.length - 1].answer,
                        reply: data.message
                    }
                };
                setHistory(newHistory);
            } else {

                // Iterate through the string to find and remove unclosed tags. E.g. if the answer is <div>Hello</div><p>World</p, the </p will be removed.

                const cleanedAnswer = data.message.replace(/<[^>]*$/g, '');

                setLocalAnswer(cleanedAnswer);
                
                scrollToBottom();
                setStreaming(true);
            }
        });

        const applyClause = async() => {
            const startTagRegex = /<article class="clause" id="([^"]+)">/g;
            const endTag = '</article>';

            let match;
            while ((match = startTagRegex.exec(element.answer.reply)) !== null) {
                const clauseId = match[1];
                const startIndex = match.index + match[0].length;
                const endIndex = element.answer.reply.indexOf(endTag, startIndex);

                const clause = clauses.find((clause) => clause.id == clauseId);

                if (endIndex !== -1) {
                    const newClause = element.answer.reply.substring(startIndex, endIndex);
                    updateContent({
                        ...clause,
                        content: newClause
                    });

                    dispatch(draftingSlice.actions.setRedraftingClause(clause));
        
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }
        }

        return (
            <div className="answer">
                <div className={`fly-questionBubble ${redraftingClause?.content === element.answer.reply ? "redrafting" : ""}`}>
                    <p className="text"> <Markdown rehypePlugins={[rehypeRaw]}>{content}</Markdown></p>
                    { content.includes('<article class="clause"') &&
                        <div className="redraft-buttons">
                            <Button 
                                text={t("drafting.apply")}
                                size={EButtonSize.SMALL}
                                onClick={applyClause}
                                iconLeft={keepIcon}
                                iconSize={16}
                                buttonType={
                                    EButtonType.SECONDARY
                                }
                                className="keep-button"
                            />
                        </div>
                    }
                    <Button 
                        text={"?"}
                        size={EButtonSize.SMALL}
                        onClick={() => redraft(t('drafting.infoQuery'), null, conversationId, redraftingClause)}
                        buttonType={
                            EButtonType.SECONDARY
                        }
                        className="info-button"
                    />
                </div>
            </div>
        )
    };

    const AttachedContent = ({content}) => {

        const revertClause = (text) => {
            updateContent({
                ...redraftingClause,
                content: text
            });
            dispatch(draftingSlice.actions.setRedraftingClause(null));
        }

        return (
            <div className="attached-content">
                <p className="text" dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(content)}}></p>
                { redraftingClause && content !== redraftingClause.content && <Button 
                    text={t("drafting.revert")}
                    size={EButtonSize.SMALL}
                    iconLeft={redraftIcon}
                    onClick={() => revertClause(content)}
                    buttonType={EButtonType.PRIMARY}
                /> }
            </div>
        )
    };

    // On click on .mc li element, send the content of the li as a redraft msg
    
    useEffect(() => {
        const mcList = document.querySelectorAll(".mc li");

        mcList.forEach((li) => {
            li.addEventListener("click", (e: any) => {
                const content = e.target.innerHTML;
                redraft(content, redraftingClause?.content, conversationId, redraftingClause);
            });
        });

        return () => {
            mcList.forEach((li) => {
                li.removeEventListener("click", (e: any) => {
                    const content = e.target.innerHTML;
                    redraft(content, redraftingClause?.content, conversationId, redraftingClause);
                });
            });
        }
    }, [redraftingClause, conversationId, history]);

    const History = (props: any) => {

        const { history } = props;

        return (
            <div ref={historyRef} className={"history " + ((redraftingClause?.instructions && !redraftingClause?.instructions_complete) ? "instructions" : "")}>
                <InitialMessage />
                {history?.map((element, index) => {

                    return (
                        <div key={index} className="fly-conversation">
                            <div className="question">
                                <span className="fly-questionBubble">
                                    { element?.answer?.type === "redraft clause" && <AttachedContent content={element?.question?.clause} />}
                                    <p className="text" dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(element?.question?.prompt)}} />
                                </span>
                            </div> 
                            <Answer element={element} />
                        </div>
                    )})
                }

            </div>
        )
    };

    useEffect(() => {

        // Send first message
        if(conversationId && 
            redraftingClause && 
            !redraftingClause.instructions_complete && 
            (redraftingClause.instructions && redraftingClause.instructions.length > 0) && 
            history.length === 0) {
            redraft(t('drafting.start_instructions_prompt', {clause: redraftingClause?.title, id: redraftingClause?.id}), redraftingClause?.content, conversationId, redraftingClause);
        }
    }, [redraftingClause, conversationId, history]);

    useEventSubscriber("copilot_new_message", conversationId, (data: any) => {
        getHistory().then(history => {
            setHistory(prevHistory => {
                const updatedHistory = [...history];
                if (updatedHistory.length > 0) {
                    updatedHistory[updatedHistory.length - 1].eventListenerId = data;
                }
                return updatedHistory;
            });
        })
    });

    const Form = (props: any) => {

        const { streaming, textareaRef } = props;

        const [localQuestion, setLocalQuestion] = useState("");

        useEffect(() => {
            setTimeout(() => {
                textareaRef?.current?.focus();
            }, 100);
        }, [textareaRef, redraftingClause]);

        const instructionsRef = useRef(null);

        useEffect(() => {
            instructionsRef?.current?.focus();
        }, [instructionsRef]);

        const handleInputChange = (event) => {
            setLocalQuestion(event.target.value);
        }
    
        const handleSubmit = (event) => {
            event.preventDefault();

            setLocalQuestion('');

            redraft(localQuestion, redraftingClause?.content, conversationId, redraftingClause);
        }
    
        const handleKeyPress = (event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
                handleSubmit(event);
            }
        }

        return (
            <form onSubmit={handleSubmit} className={redraftingClause ? "redraft-form": ""}>
                <div className="prompt-suggestions">
                    <span onClick={() => redraft(t("drafting.suggestions_button1"), redraftingClause?.content, conversationId, redraftingClause)}>{t("drafting.suggestions_button1")}</span>
                    <span onClick={() => redraft(t("drafting.suggestions_button2"), redraftingClause?.content, conversationId, redraftingClause)}>{t("drafting.suggestions_button2")}</span>
                </div>
                <textarea
                    value={localQuestion} 
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder={(redraftingClause?.instructions_complete || (!redraftingClause?.instructions || redraftingClause?.instructions?.length === 0) ? t("drafting.redraft_placeholder") : t("drafting.instructions_placeholder"))}
                    ref={textareaRef}
                    disabled={streaming}
                />
                <button type="submit" disabled={streaming}>
                    <ReactSVG src={sendIcon} />
                </button>
            </form>
        )
    }

    if(fetchingData) return null;

    return (
        <div className="co-pilot co-pilot-new">
            <History history={history} />
            <RestrictReadOnly><Form streaming={streaming} textareaRef={textareaRef} /></RestrictReadOnly>
        </div>
    );
}

export default Copilot;